import { PANEL_NAME_KEY, TRIP_STATE } from '@allocab/allocab-commons-shared-types';
import { TripDriverWithDriverOutput } from '@allocab/allocab-driver-shared-types';
import {
  BoltSolid,
  CalendarDaysSolid,
  CircleDotSolid,
  ClockSolid,
  LocationDotSolid,
  RouteSolid,
} from '@allocab/allocab-icons';
import { getPanelNameLabelByKey } from '@allocab/allocab-shared-utils';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TripAssignButton } from './trip-assign-button/TripAssignButton';
import { DEPARTURE_AT_FILTER_FORMAT, DEPARTURE_DAY_SEARCH_PARAMS_KEY } from './TripDepartureAtFilter';
import { PANEL_SEARCH_PARAMS_KEY } from './TripPanelFilter';
import { TripPrice } from './TripPrice';
import { TripsFilters } from './TripsFilters';
import { STATE_SEARCH_PARAMS_KEY } from './TripStateFilter';
import { TO_ASSIGN_SEARCH_PARAMS_KEY } from './TripToAssignFilter';
import { Link } from '../../components/atomic/Link';
import { BadgeIndicator } from '../../components/BadgeIndicator';
import { Table } from '../../components/table/Table';
import { TableColumn } from '../../components/table/types';
import { prefetchTripsPage, TRIPS_LIMIT, useFethTripsPage } from '../../hooks/useFetchTrips';
import { useFetchUser } from '../../hooks/useFetchUser';
import { t } from '../../services/translation/translation';
import { getFullName } from '../../utils/common';
import { formatDateForParisTZ, humanizeDuration } from '../../utils/date';
import { getStepForTrip, getTripStatus, shortenFormattedAddress } from '../../utils/trip';

const columns: TableColumn<TripDriverWithDriverOutput>[] = [
  {
    name: t('common.id'),
    render: trip => {
      const step = getStepForTrip(trip);

      return (
        <div className="flex flex-col items-start">
          <span>R{trip.id}</span>
          <BadgeIndicator variant={step === 'DONE' ? 'green' : step === 'CANCELLED' ? 'red' : 'yellow'}>
            {getTripStatus(trip)}
          </BadgeIndicator>
        </div>
      );
    },
  },
  {
    name: t('common.departure-at'),
    render: trip => <p className="w-36">{formatDateForParisTZ(trip.resume.departureAt, t('date.datetime'))}</p>,
    sortKey: 'resume.departureAt',
  },
  {
    name: t('common.booking'),
    render: trip =>
      trip.tripVars.flash ? (
        <span className="flex items-center gap-2 w-32">
          <BoltSolid width={16} height={16} className="fill-yellow-600" /> {t('trips-list-route.di')}
        </span>
      ) : (
        <span className="flex items-center gap-2 w-32">
          <CalendarDaysSolid width={16} height={16} className="fill-gray-500" /> {t('trips-list-route.da')}
        </span>
      ),
  },
  {
    name: t('common.panel'),
    render: trip => getPanelNameLabelByKey(trip.panelVars.key),
  },
  {
    name: t('common.itinerary'),
    render: trip => (
      <div className="w-72 space-y-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <CircleDotSolid width={12} height={12} className="fill-orange-400" />
            <p className="flex-1 min-w-0 text-ellipsis overflow-hidden">
              {shortenFormattedAddress(trip.resume.start.formattedAddress)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LocationDotSolid width={14} height={14} className="fill-current" />
            <p className="flex-1 min-w-0">{shortenFormattedAddress(trip.resume.end.formattedAddress)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <RouteSolid width={10} height={10} className="fill-current" />
            <p className="text-xs">{`~${Math.round(trip.resume.distance / 1000)} km`}</p>
          </div>
          <div className="flex items-center gap-1">
            <ClockSolid width={10} height={10} className="fill-current" />
            <p className="text-xs">{`~${humanizeDuration(trip.resume.duration)}`}</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    name: t('common.driver'),
    render: trip => (
      <Link to={`/drivers/${trip.driverKey}`}>{getFullName(trip.driverFirstName, trip.driverLastName)}</Link>
    ),
  },
  { name: t('common.account'), render: trip => trip.account.name },
  {
    name: t('trips-list-route.price'),
    render: trip =>
      trip.tripVars.servedByDedicatedDriver ? (
        <div className="rounded-lg bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-center p-2 px-3 font-bold">
          {t('common.trip-package')}
        </div>
      ) : (
        <TripPrice trip={trip} />
      ),
  },
];

const withAssignColumns: TableColumn<TripDriverWithDriverOutput>[] = [
  ...columns,
  {
    name: t('common.assign'),
    render: trip => (trip.state === 'INCOMING' ? <TripAssignButton tripKey={trip.key} /> : null),
  },
];

type Props = {
  driverKey?: string;
};

export const TripTable = ({ driverKey }: Props) => {
  const [searchParams] = useSearchParams();
  const currentPage = Math.max(parseInt(searchParams.get('page') ?? '1'), 1);
  const orderBy = searchParams.get('orderBy') ?? 'resume.departureAt';
  const direction = (searchParams.get('direction') ?? 'desc') as 'desc' | 'asc';
  const state = (searchParams.get(STATE_SEARCH_PARAMS_KEY)?.split(',') ?? undefined) as TRIP_STATE[] | undefined;
  const panelKey = (searchParams.get(PANEL_SEARCH_PARAMS_KEY)?.split(',') ?? undefined) as PANEL_NAME_KEY[] | undefined;
  const departureDay = searchParams.get(DEPARTURE_DAY_SEARCH_PARAMS_KEY);
  const toAssign = searchParams.get(TO_ASSIGN_SEARCH_PARAMS_KEY);
  const { data: user } = useFetchUser();
  const { data, isLoading, isFetching, isPreviousData } = useFethTripsPage(
    {
      direction,
      orderBy,
      state,
      panelKey,
      ...(driverKey
        ? { driverKey: [driverKey] }
        : toAssign === 'true' && user?.driverKey
        ? { driverKey: [user.driverKey], state: ['INCOMING'] }
        : {}),
      ...(departureDay
        ? {
            interval: {
              start: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').startOf('day').valueOf(),
              end: dayjs.tz(departureDay, DEPARTURE_AT_FILTER_FORMAT, 'Europe/Paris').endOf('day').valueOf(),
            },
          }
        : {}),
    },
    currentPage,
  );
  const nbPages = Math.ceil((data?.totalItems ?? 0) / TRIPS_LIMIT);

  useEffect(() => {
    if (currentPage > 1) {
      prefetchTripsPage({ direction, orderBy }, currentPage - 1);
    }
    if (currentPage < nbPages) {
      prefetchTripsPage({ direction, orderBy }, currentPage + 1);
    }
  }, [currentPage, direction, nbPages, orderBy]);

  return (
    <div>
      <TripsFilters isAssignable={driverKey ? false : true} />
      <Table
        identifier="id"
        columns={driverKey ? columns : withAssignColumns}
        data={data?.items}
        isLoading={isLoading}
        isFetching={isPreviousData && isFetching}
        nbPages={nbPages}
        context={undefined}
        totalItems={data?.totalItems ?? 0}
      />
    </div>
  );
};
